import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AlertCircle, Eye, EyeOff, Lock, LogIn, Mail } from 'lucide-react';
import { authApi } from '../../../api';
import { getDefaultRouteForRole, getSessionRole } from '../../../utils/roleUtils';

const LoginPage = ({
  error: initialError = '',
  message = '',
  onLoginSuccess,
  redirectTo = '',
  expectedRole = '',
  expectedRoleLabel = '',
  title = 'Welcome back',
  subtitle = '',
}) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(initialError);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) {
      return 'Email is required';
    }
    if (!emailRegex.test(value)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  useEffect(() => {
    if (localStorage.checkbox && localStorage.checkbox !== '') {
      setRememberMe(true);
      setEmail(localStorage.useremail || localStorage.username || '');
      setPassword(localStorage.userpassword || '');
    }
  }, []);

  const handleRememberMe = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);
    if (checked) {
      localStorage.useremail = email;
      localStorage.username = email; // keep legacy key populated
      localStorage.userpassword = password;
      localStorage.checkbox = 'true';
    } else {
      localStorage.useremail = '';
      localStorage.username = '';
      localStorage.userpassword = '';
      localStorage.checkbox = '';
    }
  };

  const togglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    setEmailError(validateEmail(email));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailValidationError = validateEmail(email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }

    if (!password.trim()) {
      setPasswordError('Password is required');
      return;
    }

    setError('');
    setPasswordError('');
    setIsLoading(true);

    try {
      const response = await authApi.login({ email, password });

      if (response.data && response.data.success) {
        if (response.data.user) {
          localStorage.setItem('session', JSON.stringify(response.data.user));
        }
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }

        if (onLoginSuccess) {
          onLoginSuccess(response.data.user);
        }

        if (expectedRole) {
          const role = getSessionRole(response.data.user);
          if (role !== expectedRole) {
            localStorage.removeItem('token');
            localStorage.removeItem('session');
            setError(`This portal is for ${expectedRoleLabel || expectedRole} accounts only.`);
            return;
          }
        }

        const nextRoute = redirectTo || getDefaultRouteForRole(response.data.user);
        navigate(nextRoute);
        window.location.reload();
      } else {
        setError(response.data?.message || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      console.error('Login error:', err);
      const apiError = err?.response?.data;
      const apiMessage = apiError?.message || apiError?.error;
      setError(apiMessage || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const logoSrc = `${process.env.PUBLIC_URL || ''}/assets/images/chat_logo.png`;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `linear-gradient(135deg, rgba(37, 211, 102, 0.65) 0%, rgba(26, 160, 80, 0.7) 50%, rgba(20, 126, 63, 0.75) 100%), url(${process.env.PUBLIC_URL || ''}/assets/images/whatsapp_bg1.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        p: 2,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 4,
          boxShadow: 10,
          backgroundColor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(6px)',
          border: '1px solid rgba(255,255,255,0.4)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Stack spacing={1} alignItems="center">
              <Box component="img" src={logoSrc} alt="ChattyBO" sx={{ height: 38 }} />
              <Typography variant="h6">{title}</Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
              {message && (
                <Alert severity="info" sx={{ width: '100%' }}>
                  {message}
                </Alert>
              )}
              {error && (
                <Alert severity="error" icon={<AlertCircle size={18} />} sx={{ width: '100%' }}>
                  {error}
                </Alert>
              )}
            </Stack>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  fullWidth
                  error={!!emailError}
                  helperText={emailError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mail size={16} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (passwordError) {
                      setPasswordError('');
                    }
                  }}
                  fullWidth
                  error={!!passwordError}
                  helperText={passwordError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock size={16} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePassword} edge="end" aria-label="toggle password visibility">
                          {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <FormControlLabel
                    control={<Checkbox checked={rememberMe} onChange={handleRememberMe} />}
                    label="Remember me"
                  />
                  <Link href="/forgot-password" underline="hover" variant="body2">
                    Forgot Password?
                  </Link>
                </Stack>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={!isLoading && <LogIn size={16} />}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} color="inherit" />
                      <span>Signing In...</span>
                    </Stack>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
        <Box sx={{ textAlign: 'center', p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Don&apos;t have an account?{' '}
            <Link href="/register" underline="hover">
              Create Account
            </Link>
          </Typography>
        </Box>
      </Card>
    </Box>
  );
};

export default LoginPage;
